import { FormState, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/src/components/ui/form";
import { useSubscribe } from "@/src/lib/actions";
import { emptyFiltersForm, FiltersFormT } from "./filters.schema";
import { useCallback } from "react";
import { CharterTypes } from "./CharterTypes";
import { Separator } from "../../../components/ui/separator";
import { API } from "./API";
import { Country } from "./Country";
import { Marina } from "./Marina";
import { YachtName } from "./YachtName";
import { useYachtsStore } from "../../../stores/yachtsStore";
import { YachtID } from "./YachtID";
import { Operator } from "./Operator";
import { BoatTypes } from "./BoatTypes";
import { APIStatuses } from "./APIStatuses";
import { Brand } from "./Brand";

export function FiltersForm({ onSave }: { onSave: () => void }) {
  const { filters, setFilters } = useYachtsStore();
  const form = useForm<FiltersFormT>({
    resolver: zodResolver(FiltersFormT),
    defaultValues: emptyFiltersForm,
    ...(filters ? { values: { ...emptyFiltersForm, ...filters } } : {}),
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const onClear = () => {
    form.reset(emptyFiltersForm);
    setFilters({});
    onSave();
  };

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        const dirtyFields = findCommonFormProps(data, form.formState.dirtyFields);
        setFilters(dirtyFields);
        onSave();
      },
      () => {
        const values = form.getValues();
        const issues = FiltersFormT.safeParse(values).error?.issues ?? [];
        console.log(issues, "errors");
      },
    ),
    [form],
  );

  useSubscribe("filters:save", onSubmit);
  useSubscribe("filters:clear", onClear);

  return (
    <Form {...form}>
      <form className="flex flex-col gap-6" onSubmit={onSubmit}>
        <div className="flex justify-between gap-x-4">
          <Operator />
          <YachtID />
        </div>
        <Separator />
        <Brand />
        <Separator />
        <CharterTypes />
        <Separator />
        <APIStatuses />
        <Separator />
        <BoatTypes />
        <Separator />
        <Country defaultValue={filters?.country} />
        <Separator />
        <Marina defaultValue={filters?.location} />
        <Separator />
        <YachtName defaultValue={filters?.yachtName} />
        <Separator />
        <API />
      </form>
    </Form>
  );
}

function findCommonFormProps(
  data: FiltersFormT,
  dirties: FormState<FiltersFormT>["dirtyFields"],
): Partial<FiltersFormT> {
  const commonProps: Partial<FiltersFormT> = {};
  for (const [key, value] of Object.entries(dirties)) {
    if ((Array.isArray(value) && !!value?.length) || (!Array.isArray(value) && !!value)) {
      (commonProps as any)[key] = (data as any)[key];
    }
  }

  commonProps.apiStatus = data.apiStatus;

  return commonProps;
}
